import { type TextareaHTMLAttributes, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Field } from './Field';

type TextareaVariant = 'ui' | 'narrative';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helper?: string;
  autoExpand?: boolean;
  variant?: TextareaVariant;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helper, autoExpand = true, variant = 'narrative', className = '', id, onChange, ...props }, ref) => {
    const textareaId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    const internalRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => internalRef.current!);

    const adjustHeight = useCallback(() => {
      const el = internalRef.current;
      if (el && autoExpand) {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      }
    }, [autoExpand]);

    useEffect(() => {
      adjustHeight();
    }, [adjustHeight, props.value]);

    const textareaClassName = [
      'field__textarea',
      variant === 'ui' ? 'field__textarea--ui' : '',
    ].filter(Boolean).join(' ');

    return (
      <Field label={label} error={error} helper={helper} id={textareaId} className={className}>
        <textarea
          ref={internalRef}
          id={textareaId}
          className={textareaClassName}
          onChange={(e) => {
            adjustHeight();
            onChange?.(e);
          }}
          {...props}
        />
      </Field>
    );
  },
);

Textarea.displayName = 'Textarea';
